'use strict';

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-predictive-search]').forEach((form) => {
    const input = form.querySelector('input[type="search"]');
    const panel = form.querySelector('[data-predictive-search-panel]');
    const list = form.querySelector('[data-predictive-search-list]');
    const viewAll = form.querySelector('[data-predictive-search-view-all]');
    const queryLabel = form.querySelector('[data-predictive-search-query]');
    const endpoint = form.dataset.predictiveSearchUrl;
    const prompt = form.querySelector('[data-search-prompt]');
    const promptWord = form.querySelector('[data-search-prompt-word]');
    const promptBrands = Array.from(form.querySelectorAll('[data-search-prompt-brand]'))
      .map((brand) => brand.textContent.trim())
      .filter((brand, index, brands) => brand && brands.indexOf(brand) === index);

    if (input && prompt && promptWord && promptBrands.length) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let promptIndex = 0;
      let promptTransitionTimer;

      const syncPrompt = () => {
        form.classList.toggle(
          'is-search-active',
          Boolean(input.value.trim()) || document.activeElement === input
        );
      };

      const rotatePrompt = () => {
        if (
          reducedMotion ||
          promptBrands.length < 2 ||
          document.hidden ||
          input.value.trim() ||
          document.activeElement === input
        ) {
          return;
        }

        promptWord.classList.add('is-leaving');
        window.clearTimeout(promptTransitionTimer);
        promptTransitionTimer = window.setTimeout(() => {
          promptIndex = (promptIndex + 1) % promptBrands.length;
          promptWord.textContent = promptBrands[promptIndex];
          promptWord.classList.remove('is-leaving');
          promptWord.classList.add('is-entering');

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => promptWord.classList.remove('is-entering'));
          });
        }, 160);
      };

      promptWord.textContent = promptBrands[0];
      prompt.hidden = false;
      form.classList.add('has-search-prompt');
      syncPrompt();

      input.addEventListener('input', syncPrompt);
      input.addEventListener('focus', syncPrompt);
      input.addEventListener('blur', syncPrompt);

      if (!reducedMotion && promptBrands.length > 1) {
        const promptTimer = window.setInterval(rotatePrompt, 2600);
        window.addEventListener('pagehide', () => {
          window.clearInterval(promptTimer);
          window.clearTimeout(promptTransitionTimer);
        }, { once: true });
      }
    }

    if (!input || !panel || !list || !viewAll || !queryLabel || !endpoint) return;

    const searchUrl = form.getAttribute('action') || '/search';
    let timer;
    let controller;
    let activeIndex = -1;
    let options = [];
    let requestNumber = 0;

    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    };

    const open = () => {
      if (!options.length) return;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const safeProductUrl = (value) => {
      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin ? `${url.pathname}${url.search}` : null;
      } catch (_error) {
        return null;
      }
    };

    const productImageUrl = (product) => {
      if (typeof product.featured_image === 'string') return product.featured_image;
      return product.featured_image?.url || product.image || '';
    };

    const setActive = (nextIndex) => {
      if (!options.length) return;

      activeIndex = (nextIndex + options.length) % options.length;
      options.forEach((option, index) => {
        const isActive = index === activeIndex;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', String(isActive));
      });

      const activeOption = options[activeIndex];
      input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    };

    const render = (products, query) => {
      list.replaceChildren();
      activeIndex = -1;

      products.forEach((product, index) => {
        const productUrl = safeProductUrl(product.url);
        if (!productUrl || !product.title) return;

        const option = document.createElement('a');
        option.id = `header-search-suggestion-${index}`;
        option.className = 'header__search-suggestion';
        option.href = productUrl;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');

        const media = document.createElement('span');
        media.className = 'header__search-suggestion-media';
        const imageUrl = productImageUrl(product);

        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 52;
          image.height = 52;
          media.append(image);
        }

        const content = document.createElement('span');
        content.className = 'header__search-suggestion-content';
        content.setAttribute('translate', 'no');

        const title = document.createElement('span');
        title.className = 'header__search-suggestion-title';
        title.textContent = product.title;
        content.append(title);

        const detailText = product.vendor || product.product_type;
        if (detailText) {
          const detail = document.createElement('span');
          detail.className = 'header__search-suggestion-detail';
          detail.textContent = detailText;
          content.append(detail);
        }

        option.append(media, content);
        list.append(option);
      });

      options = Array.from(list.querySelectorAll('[role="option"]'));
      if (!options.length) {
        close();
        return;
      }

      const allResultsUrl = new URL(searchUrl, window.location.origin);
      allResultsUrl.searchParams.set('q', query);
      allResultsUrl.searchParams.set('type', 'product');
      viewAll.href = `${allResultsUrl.pathname}${allResultsUrl.search}`;
      queryLabel.textContent = `“${query}”`;
      open();
    };

    const fetchSuggestions = async (query) => {
      controller?.abort();
      controller = new AbortController();
      const currentRequest = ++requestNumber;
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title');

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
        const payload = await response.json();
        if (currentRequest !== requestNumber || input.value.trim() !== query) return;
        render(payload.resources?.results?.products || [], query);
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();

      if (query.length < 2) {
        controller?.abort();
        close();
        return;
      }

      timer = window.setTimeout(() => fetchSuggestions(query), 180);
    });

    input.addEventListener('keydown', (event) => {
      if (panel.hidden && event.key !== 'Escape') return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex < 0 ? options.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        options[activeIndex].click();
      } else if (event.key === 'Escape') {
        close();
      }
    });

    form.addEventListener('focusin', () => {
      if (options.length && input.value.trim().length >= 2) open();
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) close();
    });
  });
});
